// Users Management — workspace access + workspace-aware role assignment (admin).
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import { Plus, Loader2, Pencil, UserX, UserCheck, Trash2, KeyRound, ShieldOff, ShieldCheck, Handshake } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const roleColors = {
  admin: 'bg-purple-100 text-purple-700',
  ls_admin: 'bg-blue-100 text-blue-700',
  agent_producer: 'bg-teal-100 text-teal-700',
  case_manager: 'bg-sky-100 text-sky-700',
  viewer: 'bg-slate-100 text-slate-700',
};

const emptyForm = { email: '', name: '', password: '', title: '', office: '', year_access: [], workspace_roles: {} };

export function UsersPage() {
  const { user, hasPermission } = useAuth();
  const canManage = hasPermission('users.manage');
  const isAdmin = user?.role === 'admin';
  const canDeactivate = hasPermission('users.deactivate') || canManage;
  const [users, setUsers] = useState([]);
  const [offices, setOffices] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [catalog, setCatalog] = useState({ standard: [], partner: [] });
  const [roleLabels, setRoleLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [mfaTarget, setMfaTarget] = useState(null);
  const [resettingMfa, setResettingMfa] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [reassignAgents, setReassignAgents] = useState([]);
  const [reassignTo, setReassignTo] = useState('');
  const [processingDeact, setProcessingDeact] = useState(false);

  const wsById = Object.fromEntries(workspaces.map((w) => [w.id, w]));
  const lsWorkspace = workspaces.find((w) => w.key === 'latinos_salud');
  const manageableWorkspaces = isAdmin ? workspaces : workspaces.filter((w) => (user?.workspace_ids || []).includes(w.id));
  const showOffice = lsWorkspace && Object.prototype.hasOwnProperty.call(form.workspace_roles, lsWorkspace.id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, c, ws] = await Promise.all([
        portalApi.getUsers({ active_only: false }),
        portalApi.getConstants().catch(() => ({})),
        portalApi.getAllWorkspaces().catch(() => []),
      ]);
      setUsers(u);
      setOffices(c.offices || []);
      setCatalog(c.workspace_role_catalog || { standard: [], partner: [] });
      setRoleLabels(c.role_labels || {});
      setWorkspaces(ws);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { document.title = 'Users · Calyvra Workspace'; load(); }, [load]);

  const initFormForUser = (u) => {
    let wr = u?.workspace_roles && Object.keys(u.workspace_roles).length ? { ...u.workspace_roles }
      : (u?.workspace_ids || []).reduce((acc, wid) => { acc[wid] = [u.role]; return acc; }, {});
    return { email: u?.email || '', name: u?.name || '', password: '', title: u?.title || '', office: u?.office || '', year_access: u?.year_access || [], workspace_roles: wr };
  };

  const openCreate = () => { setEditingId(null); setForm({ ...emptyForm, workspace_roles: {} }); setDialogOpen(true); };
  const openEdit = (u) => { setEditingId(u.id); setForm(initFormForUser(u)); setDialogOpen(true); };

  const toggleWorkspace = (wid, on) => {
    setForm((f) => {
      const wr = { ...f.workspace_roles };
      if (on) wr[wid] = wr[wid] || [];
      else delete wr[wid];
      return { ...f, workspace_roles: wr };
    });
  };

  const toggleRole = (wid, roleKey, on) => {
    setForm((f) => {
      const wr = { ...f.workspace_roles };
      const cur = new Set(wr[wid] || []);
      if (on) cur.add(roleKey); else cur.delete(roleKey);
      wr[wid] = Array.from(cur);
      return { ...f, workspace_roles: wr };
    });
  };

  const handleSave = async () => {
    if (!form.name || !form.email) { toast.error('Please fill all required fields'); return; }
    const cleaned = {};
    Object.entries(form.workspace_roles).forEach(([wid, roles]) => { if (roles && roles.length) cleaned[wid] = roles; });
    if (Object.keys(cleaned).length === 0) { toast.error('Assign at least one workspace with a role'); return; }
    setSaving(true);
    try {
      if (editingId) {
        const payload = { name: form.name, email: form.email, title: form.title, office: form.office, workspace_roles: cleaned };
        if (form.password) payload.password = form.password;
        await portalApi.updateUser(editingId, payload);
        toast.success('User updated');
      } else {
        await portalApi.createUser({ email: form.email, name: form.name, title: form.title, office: form.office, workspace_roles: cleaned });
        toast.success('User created');
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const toggleDisabled = async (u) => {
    if (!u.disabled && isAdmin) {
      try {
        const load = await portalApi.getUserClientLoad(u.id);
        if ((load.primary_count || 0) + (load.authorized_count || 0) > 0) {
          const ags = await portalApi.getAgents();
          setReassignAgents(ags.filter((a) => a.id !== u.id));
          setDeactivateTarget({ user: u, load });
          setReassignTo('');
          return;
        }
      } catch (_) { /* non-admin or no access: fall through to plain deactivate */ }
    }
    try { await portalApi.setUserActive(u.id, !u.disabled); toast.success(u.disabled ? 'User activated' : 'User deactivated'); load(); }
    catch (err) { toast.error(err.message); }
  };

  const confirmDeactivate = async (withReassign) => {
    if (!deactivateTarget) return;
    const uid = deactivateTarget.user.id;
    setProcessingDeact(true);
    try {
      if (withReassign) {
        const r = await portalApi.reassignAgentClients(uid, reassignTo || null);
        toast.success(`${r.reassigned} client(s) reassigned`);
      }
      await portalApi.setUserActive(uid, true);
      toast.success('User deactivated');
      setDeactivateTarget(null);
      load();
    } catch (err) { toast.error(err.message); } finally { setProcessingDeact(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try { await portalApi.deleteUser(deleteTarget.id); toast.success('User deleted'); setDeleteTarget(null); load(); }
    catch (err) { toast.error(err.message); setDeleteTarget(null); }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const res = await portalApi.resetUserPassword(resetTarget.id);
      toast[res.email_sent ? 'success' : 'warning'](res.email_sent ? `Temporary password emailed to ${resetTarget.email}` : 'Password reset, but email could not be sent.');
      setResetTarget(null);
    } catch (err) { toast.error(err.message); } finally { setResetting(false); }
  };

  const handleResetMfa = async () => {
    if (!mfaTarget) return;
    setResettingMfa(true);
    try { await portalApi.resetMfa(mfaTarget.id); toast.success(`MFA reset for ${mfaTarget.name}.`); setMfaTarget(null); load(); }
    catch (err) { toast.error(err.message); } finally { setResettingMfa(false); }
  };

  const userWorkspaceNames = (u) => {
    if (u.role === 'admin') return ['All workspaces'];
    const wr = u.workspace_roles && Object.keys(u.workspace_roles).length ? Object.keys(u.workspace_roles) : (u.workspace_ids || []);
    return wr.map((wid) => wsById[wid]?.name).filter(Boolean);
  };

  const userRoleEntries = (u) => {
    const wr = u.workspace_roles && Object.keys(u.workspace_roles).length ? u.workspace_roles : null;
    if (!wr) return [{ ws: '', roles: [u.role] }];
    return Object.entries(wr).map(([wid, roles]) => ({ ws: wsById[wid]?.name || '', roles: roles || [] }));
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Users</h1>
          <p className="text-slate-500 mt-1">{users.length} users · manage workspace access and roles</p>
        </div>
        <Button onClick={openCreate} data-testid="new-user-btn" className={canManage ? '' : 'hidden'}><Plus className="h-4 w-4 mr-2" />New User</Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-sky-500" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Name</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Email</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Workspaces</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Roles</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Status</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-slate-50 align-top" data-testid={`user-row-${u.email}`}>
                    <td className="px-6 py-4 font-medium text-slate-900">{u.name}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{u.email}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1" data-testid={`user-workspaces-${u.email}`}>
                        {userWorkspaceNames(u).map((n, i) => <Badge key={i} variant="outline" className="text-slate-600">{n}</Badge>)}
                        {userWorkspaceNames(u).length === 0 && <span className="text-xs text-slate-400">None</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1" data-testid={`user-roles-${u.email}`}>
                        {userRoleEntries(u).map((e, i) => (
                          <div key={i} className="flex flex-wrap items-center gap-1">
                            {e.ws && <span className="text-xs text-slate-400">{e.ws}:</span>}
                            {e.roles.map((r) => <Badge key={r} className={roleColors[r] || 'bg-slate-100 text-slate-700'}>{roleLabels[r] || r.replace('_', ' ')}</Badge>)}
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {u.disabled ? <Badge className="bg-red-100 text-red-700">Inactive</Badge> : <Badge className="bg-green-100 text-green-700">Active</Badge>}
                      <Badge className={`ml-1 ${u.mfa_enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-testid={`mfa-badge-${u.email}`}>
                        {u.mfa_enabled ? 'MFA On' : 'MFA Off'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      {canManage && <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`} title="Edit user / workspaces / roles"><Pencil className="h-4 w-4" /></Button>}
                      {canDeactivate && (
                        <Button variant="ghost" size="sm" onClick={() => toggleDisabled(u)} data-testid={`toggle-user-${u.email}`} title={u.disabled ? 'Activate' : 'Deactivate'}>
                          {u.disabled ? <UserCheck className="h-4 w-4 text-green-600" /> : <UserX className="h-4 w-4 text-red-600" />}
                        </Button>
                      )}
                      {canManage && <Button variant="ghost" size="sm" onClick={() => setResetTarget(u)} data-testid={`reset-user-${u.email}`} title="Reset password"><KeyRound className="h-4 w-4 text-amber-600" /></Button>}
                      {isAdmin && u.mfa_enabled && <Button variant="ghost" size="sm" onClick={() => setMfaTarget(u)} data-testid={`reset-mfa-${u.email}`} title="Reset MFA"><ShieldOff className="h-4 w-4 text-orange-600" /></Button>}
                      {canManage && <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(u)} data-testid={`delete-user-${u.email}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit User' : 'New User'}</DialogTitle>
            <DialogDescription>Set basic info, then choose workspaces and the role(s) within each.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
              <div className="space-y-2"><Label>Email *</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              {showOffice && (
                <div className="space-y-2"><Label>Office (Latinos Salud)</Label>
                  <Select value={form.office || 'none'} onValueChange={(v) => setForm({ ...form, office: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="user-office-select"><SelectValue placeholder="Select office" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{offices.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {editingId && (
              <div className="space-y-2"><Label>New Password (optional)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
            )}
            {!editingId && (
              <div className="rounded-lg bg-sky-50 border border-sky-100 px-3 py-2 text-sm text-sky-700" data-testid="user-temp-password-note">
                A temporary password will be emailed to the user. They'll set a new one on first login.
              </div>
            )}

            <div className="border-t border-slate-100 pt-4">
              <p className="text-sm font-medium text-slate-700 mb-1">Workspace Access &amp; Roles</p>
              <p className="text-xs text-slate-400 mb-3">Grant one or more workspaces. Assign role(s) per workspace — a role only applies in that workspace.</p>
              <div className="space-y-3" data-testid="workspace-roles-editor">
                {manageableWorkspaces.map((w) => {
                  const granted = Object.prototype.hasOwnProperty.call(form.workspace_roles, w.id);
                  const roles = form.workspace_roles[w.id] || [];
                  const roleList = catalog[w.type] || catalog.partner || [];
                  return (
                    <div key={w.id} className={`rounded-lg border p-3 ${granted ? 'border-sky-200 bg-sky-50/40' : 'border-slate-200'}`} data-testid={`ws-block-${w.key}`}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox checked={granted} onCheckedChange={(v) => toggleWorkspace(w.id, !!v)} data-testid={`ws-access-${w.key}`} />
                        <span className="font-medium text-slate-800">{w.name}</span>
                        <Badge variant="outline" className={w.type === 'standard' ? 'text-sky-700 border-sky-200' : 'text-indigo-700 border-indigo-200'}>
                          {w.type === 'standard' ? <ShieldCheck className="h-3 w-3 mr-1" /> : <Handshake className="h-3 w-3 mr-1" />}
                          {w.type === 'standard' ? 'Calyvra Insurance' : 'Partner'}
                        </Badge>
                        {!w.is_active && <Badge className="bg-slate-200 text-slate-500">Inactive</Badge>}
                      </label>
                      {granted && (
                        <div className="mt-3 pl-6 flex flex-wrap gap-x-6 gap-y-2">
                          {roleList.map((r) => {
                            const disabled = r.key === 'admin' && !isAdmin;
                            return (
                              <label key={r.key} className={`flex items-center gap-2 text-sm ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
                                <Checkbox checked={roles.includes(r.key)} disabled={disabled} onCheckedChange={(v) => toggleRole(w.id, r.key, !!v)} data-testid={`ws-role-${w.key}-${r.key}`} />
                                <span className="text-slate-700">{r.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-user-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>This permanently deletes <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email}). Reassign their cases first if needed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-user-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(resetTarget)} onOpenChange={(open) => !open && setResetTarget(null)}>
        <AlertDialogContent data-testid="reset-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset password?</AlertDialogTitle>
            <AlertDialogDescription>A temporary password will be emailed to <strong>{resetTarget?.email}</strong>. Their current password stops working immediately.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetPassword} disabled={resetting} data-testid="confirm-reset-user-btn">{resetting ? 'Sending...' : 'Reset & Email'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(mfaTarget)} onOpenChange={(open) => !open && setMfaTarget(null)}>
        <AlertDialogContent data-testid="reset-mfa-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset MFA?</AlertDialogTitle>
            <AlertDialogDescription>This removes two-factor authentication for <strong>{mfaTarget?.name}</strong>. They'll set it up again on next sign in.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetMfa} disabled={resettingMfa} className="bg-orange-600 hover:bg-orange-700" data-testid="confirm-reset-mfa-btn">{resettingMfa ? 'Resetting...' : 'Reset MFA'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={Boolean(deactivateTarget)} onOpenChange={(o) => { if (!o && !processingDeact) setDeactivateTarget(null); }}>
        <DialogContent data-testid="deactivate-reassign-dialog">
          <DialogHeader>
            <DialogTitle>Deactivate {deactivateTarget?.user?.name}</DialogTitle>
            <DialogDescription>
              This agent is the primary on <strong>{deactivateTarget?.load?.primary_count || 0}</strong> client(s)
              {deactivateTarget?.load?.authorized_count ? <> and has additional access to <strong>{deactivateTarget.load.authorized_count}</strong></> : null}.
              Reassign the primary clients before deactivating. Additional access will be removed automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reassign primary clients to</Label>
            <Select value={reassignTo || 'none'} onValueChange={(v) => setReassignTo(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="reassign-to-select"><SelectValue placeholder="Select agent" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Leave unassigned</SelectItem>
                {reassignAgents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeactivateTarget(null)} disabled={processingDeact}>Cancel</Button>
            <Button variant="ghost" className="text-red-600" onClick={() => confirmDeactivate(false)} disabled={processingDeact} data-testid="deactivate-only-btn">Deactivate without reassigning</Button>
            <Button onClick={() => confirmDeactivate(true)} disabled={processingDeact} data-testid="reassign-deactivate-btn">
              {processingDeact ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reassign & deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UsersPage;
